import { useEffect, useState } from 'react'
import { getHearingFilterOptions, getHearings } from '@services/hearings'
import { ReportEmptyState, formatReportDate, formatReportNumber } from '@components/shared/reports/ReportPrimitives'
import { defaultHearingFilters, hearingPeriodError, normalizeHearingFilters } from './hearingFilters'
import './hearings.css'

function formatConsultedAt(value) {
  if (!value) return 'Não informada'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? 'Não informada' : new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(date)
}

function FilterSelect({ label, name, value, options = [], disabled, onChange }) {
  const values = value && !options.includes(value) ? [value, ...options] : options
  const labelId = `hearings-filter-${name}-label`
  return (
    <label className="hearings-field">
      <span id={labelId}>{label}</span>
      <select name={name} value={value} aria-labelledby={labelId} onChange={(event) => onChange(name, event.target.value)} disabled={disabled}>
        <option value="">{name === 'area' ? 'Todas as áreas' : name === 'estado' ? 'Todos os estados' : 'Todos os advogados'}</option>
        {values.map((option) => <option key={option} value={option}>{option}</option>)}
      </select>
    </label>
  )
}

function HearingsTable({ items }) {
  return (
    <div className="hearings-table-wrap" role="region" aria-label="Pauta de audiências, role horizontalmente para ver todas as colunas" tabIndex={0}>
      <table className="hearings-table">
        <caption className="hearings-visually-hidden">Audiências ordenadas por data e horário, do mais próximo ao mais distante.</caption>
        <thead><tr><th scope="col">Data e hora</th><th scope="col">Processo e partes</th><th scope="col">Audiência</th><th scope="col">Advogado</th><th scope="col">Área</th><th scope="col">Estado / comarca</th><th scope="col">Foro / vara</th></tr></thead>
        <tbody>
          {items.map((item) => (
            <tr key={JSON.stringify([item.id, item.processoId, item.advogado])}>
              <td className="hearings-date"><strong>{formatReportDate(item.data)}</strong><small><i className="far fa-clock" aria-hidden="true" /> {item.hora ? String(item.hora).slice(0, 5) : 'Horário não informado'}</small></td>
              <td className="hearings-process"><strong>{item.numeroProcesso || `Processo #${item.processoId}`}</strong><small>Parte principal: {item.partePrincipal || 'Não informada'}</small><small>Parte contrária: {item.parteContraria || 'Não informada'}</small>{item.clienteNome ? <small className="hearings-client">Cliente: {item.clienteNome}</small> : null}</td>
              <td><strong>{item.tipo || 'Audiência'}</strong><span className="hearings-status">{item.status || 'Status não informado'}</span></td>
              <td>{item.advogado || 'Não informado'}</td>
              <td>{item.area || 'Não informada'}</td>
              <td><strong>{item.estado || 'Estado não informado'}</strong><small>{item.comarca || 'Comarca não informada'}</small></td>
              <td><strong>{item.foro || 'Foro não informado'}</strong><small>{item.vara || 'Vara não informada'}</small></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default function HearingsPage() {
  const [filters, setFilters] = useState(defaultHearingFilters)
  const [draft, setDraft] = useState(filters)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [result, setResult] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [filterError, setFilterError] = useState('')
  const [refreshVersion, setRefreshVersion] = useState(0)
  const [options, setOptions] = useState(null)
  const [optionsLoading, setOptionsLoading] = useState(true)
  const [optionsError, setOptionsError] = useState('')
  const [optionsVersion, setOptionsVersion] = useState(0)
  const { dataInicio, dataFim } = draft

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    setError('')
    const load = async () => {
      try {
        const response = await getHearings(filters, { page, pageSize, signal: controller.signal })
        if (controller.signal.aborted) return
        if (!response.success || !response.data) throw new Error(response.error || 'Não foi possível carregar a pauta de audiências.')
        const lastPage = Math.max(1, Math.ceil(response.data.total / response.data.tamanhoPagina))
        if (page > lastPage) {
          setPage(lastPage)
          return
        }
        setResult(response.data)
      } catch (failure) {
        if (controller.signal.aborted) return
        setError(failure.message || 'Não foi possível carregar a pauta de audiências.')
      }
      if (!controller.signal.aborted) setLoading(false)
    }
    load()
    return () => controller.abort()
  }, [filters, page, pageSize, refreshVersion])

  useEffect(() => {
    const controller = new AbortController()
    setOptions(null)
    setOptionsError('')
    if (hearingPeriodError({ dataInicio, dataFim })) {
      setOptionsLoading(false)
      return () => controller.abort()
    }
    setOptionsLoading(true)
    const load = async () => {
      try {
        const response = await getHearingFilterOptions({ dataInicio, dataFim }, { signal: controller.signal })
        if (controller.signal.aborted) return
        if (!response.success || !response.data) throw new Error(response.error || 'Não foi possível carregar as opções de filtro.')
        setOptions(response.data)
      } catch (failure) {
        if (controller.signal.aborted) return
        setOptionsError(failure.message || 'Não foi possível carregar as opções de filtro.')
      }
      if (!controller.signal.aborted) setOptionsLoading(false)
    }
    load()
    return () => controller.abort()
  }, [dataInicio, dataFim, optionsVersion, refreshVersion])

  const update = (field, value) => {
    setDraft((current) => ({ ...current, [field]: value }))
    setFilterError('')
  }
  const apply = (event) => {
    event.preventDefault()
    const validationError = hearingPeriodError(draft)
    setFilterError(validationError)
    if (validationError) return
    const next = normalizeHearingFilters(draft)
    setDraft(next)
    setFilters(next)
    setPage(1)
    setLoading(true)
  }
  const clear = () => {
    const next = defaultHearingFilters()
    setDraft(next)
    setFilters(next)
    setFilterError('')
    setPage(1)
    setLoading(true)
  }
  const reload = () => setRefreshVersion((value) => value + 1)
  const totalPages = Math.max(1, Math.ceil((result?.total || 0) / (result?.tamanhoPagina || pageSize)))
  const filtersChanged = Object.keys(filters).some((key) => draft[key].trim() !== filters[key])
  const selectedFilters = [
    filters.advogado && `Advogado: ${filters.advogado}`, filters.estado && `Estado: ${filters.estado}`,
    filters.area && `Área: ${filters.area}`, filters.numeroProcesso && `Processo: ${filters.numeroProcesso}`,
  ].filter(Boolean)
  const invalidPeriod = Boolean(hearingPeriodError(draft))
  const filterNote = invalidPeriod
    ? 'Informe um período válido para carregar as opções de filtro.'
    : optionsError
      ? 'Recarregue as opções para filtrar por advogado, estado e área.'
      : optionsLoading
        ? 'Carregando opções de advogado, estado e área...'
        : filtersChanged
          ? 'Há alterações nos filtros. Aplique para atualizar a pauta.'
          : 'Advogados, estados e áreas disponíveis no período informado.'

  return (
    <div className="hearings-page">
      <header className="hearings-heading">
        <div><span className="hearings-eyebrow">Controladoria</span><h2>Agenda processual</h2><p>Consulte as audiências dos processos do escritório por período, advogado e localização.</p></div>
        <button type="button" className="hearings-button" onClick={reload} disabled={loading}><i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-rotate-right'}`} aria-hidden="true" /> Atualizar pauta</button>
      </header>

      <section className="hearings-panel hearings-filters" aria-labelledby="hearings-filter-title">
        <div className="hearings-section-heading"><div><span className="hearings-eyebrow">Filtros</span><h2 id="hearings-filter-title">Encontre uma audiência</h2></div><span className="hearings-period-hint" id="hearings-period-hint">Período de até 366 dias</span></div>
        <form onSubmit={apply}>
          <div className="hearings-filter-grid">
            <label className="hearings-field"><span>Data inicial</span><input type="date" name="dataInicio" value={dataInicio} max={dataFim || undefined} onChange={(event) => update('dataInicio', event.target.value)} aria-describedby={`hearings-period-hint${filterError ? ' hearings-period-error' : ''}`} aria-invalid={Boolean(filterError)} required /></label>
            <label className="hearings-field"><span>Data final</span><input type="date" name="dataFim" value={dataFim} min={dataInicio || undefined} onChange={(event) => update('dataFim', event.target.value)} aria-describedby={`hearings-period-hint${filterError ? ' hearings-period-error' : ''}`} aria-invalid={Boolean(filterError)} required /></label>
            <FilterSelect label="Advogado" name="advogado" value={draft.advogado} options={options?.advogados} disabled={optionsLoading || !options || invalidPeriod} onChange={update} />
            <FilterSelect label="Estado" name="estado" value={draft.estado} options={options?.estados} disabled={optionsLoading || !options || invalidPeriod} onChange={update} />
            <FilterSelect label="Área" name="area" value={draft.area} options={options?.areas} disabled={optionsLoading || !options || invalidPeriod} onChange={update} />
            <label className="hearings-field hearings-process-filter"><span>Número do processo</span><input name="numeroProcesso" value={draft.numeroProcesso} onChange={(event) => update('numeroProcesso', event.target.value)} placeholder="Com ou sem pontuação" maxLength={100} autoComplete="off" /></label>
          </div>
          {filterError ? <p className="hearings-validation" id="hearings-period-error" role="alert">{filterError}</p> : null}
          {optionsError ? <div className="hearings-options-error" role="alert"><span>{optionsError}</span><button type="button" className="hearings-text-button" onClick={() => setOptionsVersion((value) => value + 1)}>Recarregar opções</button></div> : null}
          <div className="hearings-filter-footer"><p className="hearings-filter-note" role="status">{filterNote}</p><div className="hearings-actions"><button type="button" className="hearings-button" onClick={clear} disabled={loading}>Limpar filtros</button><button type="submit" className="hearings-button hearings-button-primary" disabled={loading}><i className="fas fa-filter" aria-hidden="true" /> Aplicar filtros</button></div></div>
        </form>
      </section>

      <section className="hearings-panel hearings-results" aria-labelledby="hearings-result-title" aria-busy={loading}>
        <div className="hearings-results-heading">
          <div><span className="hearings-eyebrow">{formatReportDate(filters.dataInicio)} a {formatReportDate(filters.dataFim)}</span><h2 id="hearings-result-title">Audiências do período</h2>{selectedFilters.length ? <p className="hearings-applied-filters">{selectedFilters.join(' · ')}</p> : null}</div>
          {!loading && !error && result ? <span className="hearings-count" role="status">{formatReportNumber(result.total)} {result.total === 1 ? 'audiência encontrada' : 'audiências encontradas'}</span> : null}
        </div>
        {loading ? <div className="hearings-loading" role="status"><i className="fas fa-spinner fa-spin" aria-hidden="true" /><span>Carregando pauta de audiências...</span></div> : error ? (
          <div className="hearings-error" role="alert"><i className="fas fa-circle-exclamation" aria-hidden="true" /><div><strong>Não foi possível carregar a pauta</strong><p>{error}</p></div><button type="button" className="hearings-button" onClick={reload}>Tentar novamente</button></div>
        ) : result?.itens?.length ? <HearingsTable items={result.itens} /> : <ReportEmptyState icon="fa-calendar-days" title="Nenhuma audiência encontrada" description="Não há audiências cadastradas para este período e os filtros selecionados." />}

        {!loading && !error && result ? <>
          <div className="hearings-pagination"><label className="hearings-page-size"><span id="hearings-page-size-label">Por página</span><select value={pageSize} aria-labelledby="hearings-page-size-label" onChange={(event) => { setPageSize(Number(event.target.value)); setPage(1); setLoading(true) }}><option value={25}>25</option><option value={50}>50</option><option value={100}>100</option></select></label><nav className="hearings-page-navigation" aria-label="Paginação das audiências"><span>Página {result.pagina} de {totalPages}</span><button type="button" className="hearings-button" disabled={result.pagina <= 1} onClick={() => { setPage(result.pagina - 1); setLoading(true) }}><i className="fas fa-chevron-left" aria-hidden="true" /> Anterior</button><button type="button" className="hearings-button" disabled={result.pagina >= totalPages} onClick={() => { setPage(result.pagina + 1); setLoading(true) }}>Próxima <i className="fas fa-chevron-right" aria-hidden="true" /></button></nav></div>
          <p className="hearings-consulted"><i className="far fa-clock" aria-hidden="true" /> Consultado em {formatConsultedAt(result.consultadoEm)}. Datas e horários conforme o cadastro da audiência.</p>
        </> : null}
      </section>
    </div>
  )
}
